import React, { useMemo } from 'react';
import { CalendarDays } from 'lucide-react';
import {
  buildMonthlyDays, summarizeMonthlyDays, formatAttendanceTime, STATUS_LABELS,
} from '../../lib/attendanceUtils';

const STATUS_STYLES = {
  early: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  on_time: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  present: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  late: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  half_day: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  absent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  missing_clock_in: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  no_record: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300',
  on_leave: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  public_holiday: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  rest_day: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
  weekend: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
  upcoming: 'bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
};
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const Stat = ({ label, value, tone = 'text-slate-900 dark:text-slate-100', testId }) => (
  <div className="rounded-lg bg-white/70 dark:bg-slate-900/40 px-3 py-2">
    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
    <p className={`text-lg font-bold ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export const EmployeeMonthlyReport = ({
  employee, month, records, approvedLeaves, publicHolidays, canEdit = false, onEditDay, onMarkPresent,
}) => {
  const days = useMemo(() => buildMonthlyDays({
    employeeId: employee?.employee_id, month, records, approvedLeaves, publicHolidays,
  }), [employee?.employee_id, month, records, approvedLeaves, publicHolidays]);
  const summary = useMemo(() => summarizeMonthlyDays(days), [days]);
  const monthLabel = new Date(`${month}-01T00:00:00`).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="employee-monthly-report">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-violet-500" />
            <span data-testid="employee-monthly-report-name">{employee?.full_name || 'Employee'}</span>
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {employee?.employee_id}{employee?.department ? ` · ${employee.department}` : ''}{employee?.position ? ` · ${employee.position}` : ''}
          </p>
        </div>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200" data-testid="employee-monthly-report-month">{monthLabel}</p>
      </div>

      <div className="p-4 bg-gradient-to-r from-violet-50 to-sky-50 dark:from-violet-900/20 dark:to-sky-900/20 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="employee-monthly-summary">
        <Stat label="Working Days" value={summary.workingDays} testId="emr-working-days" />
        <Stat label="Days Present" value={summary.daysPresent} tone="text-emerald-700 dark:text-emerald-300" testId="emr-days-present" />
        <Stat label="Late" value={summary.late} tone="text-amber-700 dark:text-amber-300" testId="emr-late" />
        <Stat label="Early Departures" value={summary.earlyDepartures} tone="text-rose-700 dark:text-rose-300" testId="emr-early-departures" />
        <Stat label="On Leave" value={summary.onLeave} tone="text-sky-700 dark:text-sky-300" testId="emr-on-leave" />
        <Stat label="Public Holidays" value={summary.publicHolidays} tone="text-indigo-700 dark:text-indigo-300" testId="emr-public-holidays" />
        <Stat label="No Record" value={summary.noRecord} tone="text-red-600 dark:text-red-300" testId="emr-no-record" />
        <Stat label="Total Hours" value={summary.totalHours} testId="emr-total-hours" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
            <tr>
              {['Date', 'Day', 'Clock In', 'Clock Out', 'Hours', 'Status', 'Remarks', ...(canEdit ? ['Actions'] : [])].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {days.map((d) => {
              const muted = ['rest_day', 'weekend', 'upcoming'].includes(d.status);
              return (
                <tr key={d.date} data-testid={`emr-day-${d.date}`} className={`${muted ? 'bg-slate-50/60 dark:bg-slate-900/30' : ''} hover:bg-slate-50 dark:hover:bg-slate-700/40`}>
                  <td className="px-4 py-2.5 text-sm font-medium text-slate-800 dark:text-slate-100">{d.date}</td>
                  <td className={`px-4 py-2.5 text-sm ${d.dow === 0 ? 'text-rose-500' : 'text-slate-600 dark:text-slate-300'}`}>{DOW[d.dow]}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200">{formatAttendanceTime(d.record?.clock_in, { withMeridiem: true })}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200">{formatAttendanceTime(d.record?.clock_out, { withMeridiem: true })}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200">{d.hours}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      <span data-testid={`emr-status-${d.date}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[d.status] || STATUS_STYLES.present}`}>
                        {STATUS_LABELS[d.status] || d.status}
                      </span>
                      {d.hrMarked && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                          Marked by HR{d.presenceReason ? ` · ${d.presenceReason}` : ''}
                        </span>
                      )}
                      {d.earlyDeparture && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">Early Departure</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                    {[d.holiday?.name, d.record && d.leave ? `Approved ${d.leaveLabel}` : d.leaveLabel, d.record?.notes].filter(Boolean).join(' · ') || '—'}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-2.5">
                      {d.record ? (
                        <button type="button" data-testid={`emr-edit-${d.date}`} onClick={() => onEditDay?.(d.record)} className="px-3 py-1 rounded-lg text-xs font-medium border border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/30">Edit</button>
                      ) : (!d.isFuture && d.status !== 'rest_day' && d.status !== 'public_holiday') ? (
                        <button type="button" data-testid={`emr-mark-present-${d.date}`} onClick={() => onMarkPresent?.(d.date)} className="px-3 py-1 rounded-lg text-xs font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30">Mark Present</button>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
